import { useDeleteCurrentUserFavoriteMutation } from 'gcs-common/clients/api/entities/current-user/current-user-api';
import IconComponent from 'gcs-common/components/Icon/Icon';
import { ICON, ICON_COLOR } from 'gcs-common/constants/IconConstants';
import { MESSAGE_TYPE } from 'gcs-common/constants/MessageTypes';
import scrollToMessageId from 'gcs-common/slices/chat/chatThunks/scrollToMessageId';
import { getCurrentUserId } from 'gcs-common/slices/currentUser/currentUserSelectors';
import { getMessage, getMessageLoaded } from 'gcs-common/slices/messages/messageSelector';
import fetchMessage from 'gcs-common/slices/messages/messagesThunks/fetchMessage';
import { getUserName } from 'gcs-common/slices/user/userSelectors';
import type { Response } from 'gcs-common/types/typeHelper';
import moment from 'moment';
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import AsyncMessageLoader from '../../AsyncMessageLoader/AsyncMessageLoader';
import MediaMessage from '../../MediaMessage/MediaMessage';
import DeletedMessage from '../../Message/DeletedMessage/DeletedMessage';
import VideoMessage from '../../VideoMessage/VideoMessage';
import styles from './styles.module.scss';

type Favorite = Response<'/chat/favorites', 'get'>['favorites'][number];

const Favorite = ({ favorite }: { favorite: Favorite }) => {
  const dispatch = useDispatch();
  const currentUserId = useSelector(getCurrentUserId);
  const { channelId, messageId } = favorite;

  const isMessageLoaded = useSelector(getMessageLoaded(messageId));
  const message = useSelector(getMessage(messageId));

  const deleteFavoriteMutation = useDeleteCurrentUserFavoriteMutation();

  useEffect(() => {
    if (!isMessageLoaded && messageId) {
      // @ts-ignore -> remove thunk
      dispatch(fetchMessage({ messageId }));
    }
  }, [dispatch, isMessageLoaded, messageId]);

  const { text, index, authorId, dateUpdated, messageType, deleted, forwardedMessageText } = message || {};

  const time = moment(dateUpdated);

  const fromMe = currentUserId === authorId;
  const userName = useSelector(getUserName(authorId)) || '';

  const scrollToMessage = useCallback(() => {
    // @ts-expect-error -> remove thunk
    dispatch(scrollToMessageId({ channelId, messageId, messageIndex: index }));
  }, [channelId, dispatch, index, messageId]);

  const handleFavoriteRemoved = () => {
    deleteFavoriteMutation.mutate({ favoriteId: favorite.id });
  };

  if (!messageId) {
    return null;
  }

  let content = null;

  // TODO: SMOOT-2845 Fix
  if (messageType === MESSAGE_TYPE.MEDIA || messageType === MESSAGE_TYPE.MEDIA_FROM_MAIL) {
    content = (
      <AsyncMessageLoader channelId={channelId} messageIndex={index} messageId={messageId}>
        <MediaMessage messageId={messageId} selectionDisabled />
      </AsyncMessageLoader>
    );
  } else if (messageType === MESSAGE_TYPE.VIDEO) {
    content = (
      <AsyncMessageLoader channelId={channelId} messageIndex={index} messageId={messageId}>
        <VideoMessage messageId={messageId} selectionDisabled />
      </AsyncMessageLoader>
    );
  } else if (deleted) {
    // Should never happen, just in case
    content = <DeletedMessage />;
  } else {
    content = text || forwardedMessageText;
  }

  return (
    <div key={favorite.messageId} className={`${styles.favoriteWrapper} ${fromMe ? styles.me : ''}`}>
      <button
        type="button"
        onClick={handleFavoriteRemoved}
        className={styles.favoriteIconButton}
        disabled={deleteFavoriteMutation.isPending}
      >
        <IconComponent Icon={ICON.STAR_FILLED} color={ICON_COLOR.PRIMARY} style={{ marginLeft: '-4px' }} />
      </button>
      <button type="button" onClick={scrollToMessage} className={styles.favoriteContent}>
        <div className={styles.userName}>{userName}</div>
        <div className={styles.content}>{content}</div>
        <div className={styles.favoriteDate}>{time.format('LLL')}</div>
      </button>
    </div>
  );
};

export default Favorite;
